// Recording entity handler with batch operations

import { IEntityHandler, MigrationOperation, MigrationResponse, BatchResult } from '../interfaces';
import { MigrationClient } from '../MigrationClient';
import { RecordingTransformer } from '../transformers/RecordingTransformer';

export class RecordingHandler implements IEntityHandler {
  private migrationClient = MigrationClient.getInstance();
  private transformer = new RecordingTransformer();

  async handleOperation(
    operation: MigrationOperation,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    console.log(`[RECORDING-HANDLER] ${operation} operation for recording ${amplifyData.id || 'unknown'}`);
    
    // Extract the actual recording data from Amplify result or use input data
    const recordingData = (amplifyResult as Record<string, Record<string, unknown>>)?.data?.createRecording || amplifyData;
    
    // Transform the data
    const transformedData = await this.transformer.transform(recordingData as Record<string, unknown>);
    console.log(`[RECORDING-HANDLER] Transformed data:`, transformedData);
    // Send to migration service
    return await this.migrationClient.invoke(operation, 'Recording', transformedData);
  }

  // Upsert recording when viewed to capture transcription/segments that arrive after creation
  async upsertRecordingOnView(recordingId: string, amplifyRecordingData?: Record<string, unknown>): Promise<MigrationResponse> {
    try {
      console.log(`[RECORDING-HANDLER] Upserting recording ${recordingId} on view`);
      
      // If no data provided, this is just a trigger to update from Amplify
      if (!amplifyRecordingData) {
        console.log(`[RECORDING-HANDLER] No data provided for ${recordingId}, skipping upsert`);
        return {
          success: true,
          operation: 'UPDATE',
          entity_type: 'Recording',
          skipped: true,
          reason: 'No recording data provided',
          timestamp: new Date().toISOString()
        };
      }

      // Transform and update
      const transformedData = await this.transformer.transform(amplifyRecordingData);
      console.log(`[RECORDING-HANDLER] Transformed data:`, transformedData);
      // Use UPDATE operation to ensure we capture any new transcription/segments
      return await this.migrationClient.invoke('UPDATE', 'Recording', {
        id: recordingId,
        ...transformedData,
        // Force update timestamp to ensure migration happens
        updated_at: new Date().toISOString()
      });
    } catch (error) {
      console.error(`[RECORDING-HANDLER] Failed to upsert recording ${recordingId}:`, error);
      return {
        success: false,
        operation: 'UPDATE',
        entity_type: 'Recording',
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Batch upsert recordings when viewing recording lists
  async batchUpsertRecordingsOnView(recordings: Array<{ id: string; data?: Record<string, unknown> }>): Promise<BatchResult> {
    console.log(`[RECORDING-HANDLER] Batch upserting ${recordings.length} recordings on view`);
    
    let successful = 0;
    let failed = 0;
    const errors: string[] = [];

    // Process in small batches to avoid overwhelming the system
    const batchSize = 3;
    for (let i = 0; i < recordings.length; i += batchSize) {
      const batch = recordings.slice(i, i + batchSize);
      
      await Promise.all(
        batch.map(async (recording) => {
          try {
            const result = await this.upsertRecordingOnView(recording.id, recording.data);
            if (result.success) {
              successful++;
            } else {
              failed++;
              if (result.error) errors.push(`${recording.id}: ${result.error}`);
            }
          } catch (error) {
            failed++;
            errors.push(`${recording.id}: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        })
      );

      // Small delay between batches
      if (i + batchSize < recordings.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    console.log(`[RECORDING-HANDLER] Batch completed: ${successful} successful, ${failed} failed`);
    return { successful, failed, errors };
  }
} 